import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Calendar, CalendarRange, Download, RefreshCw, Trash2, PlayCircle, ChevronDown,
    ChevronRight, AlertTriangle, CheckCircle2, XCircle, Link as LinkIcon, Search, Upload,
} from "lucide-react";
import { SCSection, fmtDate } from "./Common";
import {
    scAdminNavHistFetch, scAdminNavHistList, scAdminNavHistGet,
    scAdminNavHistRefresh, scAdminNavHistImport, scAdminNavHistDelete,
    scAdminNavHistDownloadUrl, scAdminNavHistListMap,
    scAdminNavHistSaveMap, scAdminNavHistDeleteMap, scAdminListSifs,
    scAdminNavHistUpload,
} from "../../../lib/sifcontrolApi";

const STATUS_STYLES = {
    fetching: "bg-purple-100 text-purple-800",
    staged: "bg-blue-100 text-blue-800",
    imported: "bg-green-100 text-green-800",
    parse_failed: "bg-red-100 text-red-800",
    fetch_failed: "bg-red-100 text-red-800",
    empty: "bg-yellow-100 text-yellow-800",
};
const ACTION_STYLES = {
    insert: "bg-green-100 text-green-800",
    update: "bg-blue-100 text-blue-800",
    skip_duplicate: "bg-oth-bg-2 text-oth-ink3",
    unmatched: "bg-red-100 text-red-800",
    variant_missing: "bg-yellow-100 text-yellow-800",
};
const ACTION_LABELS = {
    insert: "Insert",
    update: "Update",
    skip_duplicate: "Duplicate",
    unmatched: "Unmatched",
    variant_missing: "Variant missing",
};

const Pill = ({ text, cls }) => (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-body uppercase tracking-wider ${cls || "bg-oth-bg-2 text-oth-ink3"}`}>{text}</span>
);

const fmtISODate = (d) => {
    if (!d) return "—";
    try {
        return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return d; }
};

/* -------------------------- Fetch card -------------------------- */
const FetchCard = ({ onFetched }) => {
    const today = new Date().toISOString().slice(0, 10);
    const [mode, setMode] = useState("single"); // single | range
    const [date, setDate] = useState(today);
    const [start, setStart] = useState(today);
    const [end, setEnd] = useState(today);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(null);

    const fetchSingle = async () => {
        if (!date) { toast.error("Pick a date"); return; }
        setBusy(true);
        try {
            const s = await scAdminNavHistFetch({ date });
            toast.success(`Fetching AMFI data for ${fmtISODate(date)}… this can take up to 90 seconds.`);
            onFetched?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Fetch failed. Check the date is a business day and try again.");
        } finally { setBusy(false); }
    };
    const fetchRange = async () => {
        if (!start || !end) { toast.error("Pick a start and end date"); return; }
        setBusy(true); setProgress({ started: true });
        try {
            const r = await scAdminNavHistFetch({ start_date: start, end_date: end });
            setProgress(r);
            toast.success(`Queued ${r.queued} dates. Fetches will run in the background — refresh the list to see progress.`);
            onFetched?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Range fetch failed.");
        } finally { setBusy(false); }
    };

    return (
        <div className="border border-oth-line/70 rounded-3xl bg-white p-5 mb-6" data-testid="navhist-fetch-card">
            <div className="flex items-center gap-2 mb-4">
                <Download size={18} className="text-oth-blue" />
                <div className="font-display text-lg font-semibold tracking-tight">Fetch from AMFI</div>
            </div>
            <div className="flex items-center gap-4 mb-4">
                <button
                    onClick={() => setMode("single")}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-body ${mode === "single" ? "bg-[#0A0A0A] text-white" : "bg-oth-bg-2 text-oth-ink2"}`}
                    data-testid="navhist-mode-single"
                >
                    <Calendar size={13} /> Single date
                </button>
                <button
                    onClick={() => setMode("range")}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-body ${mode === "range" ? "bg-[#0A0A0A] text-white" : "bg-oth-bg-2 text-oth-ink2"}`}
                    data-testid="navhist-mode-range"
                >
                    <CalendarRange size={13} /> Date range (business days)
                </button>
            </div>
            {mode === "single" ? (
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
                    <label className="block">
                        <div className="text-[11px] uppercase tracking-[0.2em] text-oth-ink3 font-body mb-1.5">NAV Date</div>
                        <input
                            type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)}
                            className="w-full bg-white border border-oth-line/70 rounded-2xl px-4 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue"
                            data-testid="navhist-single-date"
                        />
                    </label>
                    <button
                        onClick={fetchSingle} disabled={busy}
                        className="px-5 py-2.5 rounded-full bg-oth-blue text-white text-sm font-body font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                        data-testid="navhist-fetch-single-btn"
                    >
                        {busy ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />} Fetch Excel
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-end">
                    <label className="block">
                        <div className="text-[11px] uppercase tracking-[0.2em] text-oth-ink3 font-body mb-1.5">Start</div>
                        <input type="date" value={start} max={today} onChange={(e) => setStart(e.target.value)}
                            className="w-full bg-white border border-oth-line/70 rounded-2xl px-4 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue"
                            data-testid="navhist-range-start" />
                    </label>
                    <label className="block">
                        <div className="text-[11px] uppercase tracking-[0.2em] text-oth-ink3 font-body mb-1.5">End</div>
                        <input type="date" value={end} max={today} onChange={(e) => setEnd(e.target.value)}
                            className="w-full bg-white border border-oth-line/70 rounded-2xl px-4 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue"
                            data-testid="navhist-range-end" />
                    </label>
                    <button
                        onClick={fetchRange} disabled={busy}
                        className="px-5 py-2.5 rounded-full bg-oth-blue text-white text-sm font-body font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                        data-testid="navhist-fetch-range-btn"
                    >
                        {busy ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />} Fetch Range
                    </button>
                </div>
            )}
            <div className="mt-3 text-[11px] font-body text-oth-ink3">
                Uses AMFI's "All NAV's for a date" page. Range mode iterates business days only (Mon–Fri), capped at 60 dates per run.
            </div>
            {progress?.results && (
                <div className="mt-4 rounded-2xl bg-oth-bg-2/50 border border-oth-line/60 p-3 max-h-40 overflow-y-auto text-[11px] font-mono">
                    {progress.results.map((r) => (
                        <div key={r.date} className={r.ok ? "text-green-700" : "text-red-700"}>
                            {r.ok ? "✓" : "✗"} {r.date} {r.ok ? "staged" : `— ${r.error}`}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/* -------------------------- Preview modal -------------------------- */
const PreviewModal = ({ stage, onClose, onImported, onChanged }) => {
    const [filter, setFilter] = useState("all"); // all|insert|update|dup|unmatched|variant
    const [detail, setDetail] = useState(stage);
    const [importing, setImporting] = useState(false);
    const [showMap, setShowMap] = useState(null); // amfi row awaiting map
    const [sifOpts, setSifOpts] = useState([]);
    const [sifQuery, setSifQuery] = useState("");
    const [chosenCode, setChosenCode] = useState("");

    useEffect(() => { setDetail(stage); }, [stage]);
    useEffect(() => {
        (async () => {
            try {
                const r = await scAdminListSifs({ page: 1, page_size: 500 });
                const list = r?.items || r?.results || [];
                setSifOpts(list.map((s) => ({ code: s.sif_code, name: s.sif_name })));
            } catch { /* non-fatal */ }
        })();
    }, []);

    const filteredSifOpts = useMemo(() => {
        const q = sifQuery.trim().toLowerCase();
        if (!q) return sifOpts.slice(0, 40);
        return sifOpts.filter(o => (o.code || "").toLowerCase().includes(q) || (o.name || "").toLowerCase().includes(q)).slice(0, 40);
    }, [sifOpts, sifQuery]);

    if (!detail) return null;

    const counts = detail.counts || {};
    const preview = detail.preview || [];
    const rows = preview.filter((r) => {
        if (filter === "all") return true;
        if (filter === "dup") return r.action === "skip_duplicate";
        if (filter === "variant") return r.action === "variant_missing";
        return r.action === filter;
    });
    const groupedUnmatched = detail.unmatched_scheme_names || [];

    const doImport = async () => {
        if (!window.confirm(`Import into database?\n\n${counts.insert || 0} to insert, ${counts.update || 0} to update, ${counts.skip_duplicate || 0} duplicates skipped, ${counts.unmatched || 0} unmatched.`)) return;
        setImporting(true);
        try {
            const s = await scAdminNavHistImport(detail.id);
            toast.success(`Inserted ${s.inserted} · Updated ${s.updated} · Duplicates ${s.skipped_duplicate} · Unmatched ${s.skipped_unmatched}`);
            onImported?.();
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Import failed");
        } finally { setImporting(false); }
    };
    const doRefresh = async () => {
        try {
            const d = await scAdminNavHistRefresh(detail.id);
            setDetail(d);
            toast.success("Preview refreshed");
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
    };
    const openMap = (grp) => { setShowMap(grp); setChosenCode(""); setSifQuery(""); };
    const saveMap = async () => {
        if (!chosenCode) { toast.error("Pick a SIF to map to"); return; }
        try {
            await scAdminNavHistSaveMap(showMap.amfi_scheme_name, chosenCode);
            toast.success("Mapping saved. Refreshing preview…");
            setShowMap(null);
            const d = await scAdminNavHistRefresh(detail.id);
            setDetail(d);
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-start md:items-center justify-center px-3 py-6 md:p-8 bg-black/50 backdrop-blur-sm" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="w-full bg-white rounded-3xl border border-oth-line/70 shadow-2xl max-h-[92vh] overflow-hidden flex flex-col" style={{ maxWidth: 1100 }} data-testid="navhist-preview-modal">
                <div className="flex items-center justify-between px-6 py-4 border-b border-oth-line/70">
                    <div>
                        <div className="font-display text-lg font-semibold tracking-tight">
                            Preview — {fmtISODate(detail.target_date)}
                        </div>
                        <div className="text-[11px] font-body text-oth-ink3 mt-0.5">{detail.filename} · {detail.rows_total} rows</div>
                    </div>
                    <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-oth-bg-2 flex items-center justify-center" data-testid="navhist-preview-close">
                        <XCircle size={16} />
                    </button>
                </div>

                {/* Counts strip */}
                <div className="grid grid-cols-5 gap-3 px-6 py-4 border-b border-oth-line/70 bg-oth-bg-2/40">
                    {[
                        { k: "insert", label: "Insert", val: counts.insert || 0 },
                        { k: "update", label: "Update", val: counts.update || 0 },
                        { k: "dup", label: "Duplicate", val: counts.skip_duplicate || 0 },
                        { k: "variant", label: "Variant missing", val: counts.variant_missing || 0 },
                        { k: "unmatched", label: "Unmatched", val: counts.unmatched || 0 },
                    ].map((c) => (
                        <button key={c.k} onClick={() => setFilter(c.k === "dup" ? "dup" : c.k)}
                            className={`rounded-2xl border p-3 text-left ${filter === c.k ? "border-oth-blue bg-white" : "border-oth-line/50 bg-white hover:bg-white/60"}`}
                            data-testid={`navhist-count-${c.k}`}>
                            <div className="text-[10px] uppercase tracking-[0.15em] text-oth-ink3 font-body">{c.label}</div>
                            <div className="font-display text-xl font-semibold text-oth-ink">{c.val}</div>
                        </button>
                    ))}
                </div>

                {/* Unmatched grouped names + map action */}
                {groupedUnmatched.length > 0 && (
                    <div className="px-6 py-4 border-b border-oth-line/70 bg-yellow-50/60">
                        <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle size={14} className="text-yellow-800" />
                            <div className="font-body text-sm font-medium text-yellow-900">
                                {groupedUnmatched.length} AMFI scheme name{groupedUnmatched.length === 1 ? "" : "s"} could not be auto-matched
                            </div>
                        </div>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto thin-scroll">
                            {groupedUnmatched.map((g) => (
                                <div key={g.amfi_scheme_name_norm} className="flex items-center justify-between text-[12px] font-body">
                                    <div className="text-oth-ink2 truncate mr-3">
                                        {g.amfi_scheme_name} <span className="text-oth-ink3">({g.variants.length} variant{g.variants.length === 1 ? "" : "s"})</span>
                                    </div>
                                    <button onClick={() => openMap(g)} className="px-3 py-1 rounded-full bg-oth-blue text-white text-[11px] font-body flex items-center gap-1" data-testid={`navhist-map-btn-${g.amfi_scheme_name_norm}`}>
                                        <LinkIcon size={11} /> Map to SIF
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Filter bar */}
                <div className="px-6 py-2.5 border-b border-oth-line/70 flex items-center gap-2 text-[11px] font-body">
                    <span className="text-oth-ink3">Show:</span>
                    {[
                        { k: "all", label: "All" },
                        { k: "insert", label: "Insert" },
                        { k: "update", label: "Update" },
                        { k: "dup", label: "Duplicate" },
                        { k: "variant", label: "Variant missing" },
                        { k: "unmatched", label: "Unmatched" },
                    ].map((f) => (
                        <button key={f.k} onClick={() => setFilter(f.k)} className={`px-2.5 py-1 rounded-full ${filter === f.k ? "bg-[#0A0A0A] text-white" : "bg-oth-bg-2 text-oth-ink2"}`} data-testid={`navhist-filter-${f.k}`}>
                            {f.label}
                        </button>
                    ))}
                    <div className="ml-auto text-oth-ink3">{rows.length} of {preview.length} shown</div>
                </div>

                {/* Preview table */}
                <div className="flex-1 overflow-auto thin-scroll">
                    <table className="w-full text-[12px] font-body">
                        <thead className="sticky top-0 bg-white border-b border-oth-line/70 text-left text-oth-ink3">
                            <tr>
                                <th className="px-4 py-2 font-medium">AMFI Scheme</th>
                                <th className="px-2 py-2 font-medium">Plan</th>
                                <th className="px-2 py-2 font-medium">Option</th>
                                <th className="px-2 py-2 font-medium text-right">New NAV</th>
                                <th className="px-2 py-2 font-medium text-right">Existing</th>
                                <th className="px-2 py-2 font-medium">Match</th>
                                <th className="px-3 py-2 font-medium">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr><td colSpan={7} className="px-4 py-10 text-center text-oth-ink3">Nothing to show for this filter.</td></tr>
                            )}
                            {rows.map((r, idx) => (
                                <tr key={idx} className="border-b border-oth-line/40 hover:bg-oth-bg-2/40">
                                    <td className="px-4 py-2 max-w-[300px]"><div className="truncate">{r.amfi_scheme_name}</div></td>
                                    <td className="px-2 py-2">{r.plan}</td>
                                    <td className="px-2 py-2">{r.option}</td>
                                    <td className="px-2 py-2 text-right tabular-nums">{r.nav?.toFixed?.(4) ?? r.nav}</td>
                                    <td className="px-2 py-2 text-right tabular-nums text-oth-ink3">{r.existing_nav != null ? r.existing_nav.toFixed(4) : "—"}</td>
                                    <td className="px-2 py-2 text-oth-ink3 truncate max-w-[180px]">{r.sif_code || "—"}</td>
                                    <td className="px-3 py-2"><Pill text={ACTION_LABELS[r.action] || r.action} cls={ACTION_STYLES[r.action]} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Footer actions */}
                <div className="px-6 py-4 border-t border-oth-line/70 bg-oth-bg-2/40 flex items-center justify-between gap-3">
                    <div className="text-[11px] font-body text-oth-ink3">
                        Imports are idempotent — running again on the same date only inserts new / changed values.
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={doRefresh} className="px-3 py-2 rounded-full bg-white border border-oth-line/70 text-xs font-body hover:bg-oth-bg-2 flex items-center gap-1.5" data-testid="navhist-refresh-btn">
                            <RefreshCw size={12} /> Refresh preview
                        </button>
                        <a href={scAdminNavHistDownloadUrl(detail.id)} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-full bg-white border border-oth-line/70 text-xs font-body hover:bg-oth-bg-2 flex items-center gap-1.5" data-testid="navhist-download-btn">
                            <Download size={12} /> Download XLSX
                        </a>
                        <button
                            onClick={doImport} disabled={importing || detail.status === "imported"}
                            className="px-4 py-2 rounded-full bg-oth-blue text-white text-xs font-body font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
                            data-testid="navhist-import-btn">
                            {importing ? <RefreshCw size={12} className="animate-spin" /> : <PlayCircle size={12} />}
                            {detail.status === "imported" ? "Already imported" : "Import into DB"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Map picker sub-modal */}
            {showMap && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center px-3 py-6 bg-black/50 backdrop-blur-sm" onClick={() => setShowMap(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="w-full bg-white rounded-3xl border border-oth-line/70 shadow-2xl max-w-[540px] flex flex-col" data-testid="navhist-map-modal">
                        <div className="px-6 py-4 border-b border-oth-line/70">
                            <div className="font-display text-lg font-semibold tracking-tight">Map to SIF</div>
                            <div className="text-[12px] font-body text-oth-ink2 mt-1">
                                AMFI scheme: <span className="font-medium text-oth-ink">{showMap.amfi_scheme_name}</span>
                            </div>
                        </div>
                        <div className="p-5">
                            <div className="relative mb-3">
                                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-oth-ink3" />
                                <input value={sifQuery} onChange={(e) => setSifQuery(e.target.value)} placeholder="Search SIFs by name or code…"
                                    className="w-full bg-white border border-oth-line/70 rounded-2xl pl-9 pr-4 py-2.5 text-sm font-body focus:outline-none focus:border-oth-blue"
                                    data-testid="navhist-map-search" />
                            </div>
                            <div className="max-h-[320px] overflow-y-auto thin-scroll border border-oth-line/50 rounded-2xl">
                                {filteredSifOpts.map((o) => (
                                    <label key={o.code} className={`flex items-center gap-3 px-3 py-2 border-b border-oth-line/30 last:border-b-0 cursor-pointer text-[13px] font-body ${chosenCode === o.code ? "bg-oth-blue/10" : "hover:bg-oth-bg-2/50"}`}>
                                        <input type="radio" name="sif-pick" value={o.code} checked={chosenCode === o.code} onChange={() => setChosenCode(o.code)} data-testid={`navhist-map-opt-${o.code}`} />
                                        <div className="flex-1">
                                            <div className="text-oth-ink">{o.name || "—"}</div>
                                            <div className="text-oth-ink3 text-[11px]">{o.code}</div>
                                        </div>
                                    </label>
                                ))}
                                {filteredSifOpts.length === 0 && <div className="p-6 text-center text-oth-ink3 text-sm">No SIFs match your search.</div>}
                            </div>
                        </div>
                        <div className="px-5 py-4 border-t border-oth-line/70 bg-oth-bg-2/40 flex items-center justify-end gap-3">
                            <button onClick={() => setShowMap(null)} className="px-4 py-2 rounded-full text-sm font-body text-oth-ink2 hover:bg-oth-bg-2">Cancel</button>
                            <button onClick={saveMap} disabled={!chosenCode} className="px-4 py-2 rounded-full bg-oth-blue text-white text-sm font-body font-medium hover:opacity-90 disabled:opacity-50" data-testid="navhist-map-save">
                                Save mapping
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/* -------------------------- Mappings drawer -------------------------- */
const MappingsSection = () => {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);

    const load = async () => {
        try { const r = await scAdminNavHistListMap(); setItems(r?.items || []); } catch { /* ignore */ }
    };
    useEffect(() => { load(); }, []);
    const remove = async (norm) => {
        if (!window.confirm("Remove this mapping?")) return;
        try { await scAdminNavHistDeleteMap(norm); toast.success("Mapping removed"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };
    return (
        <div className="border border-oth-line/70 rounded-3xl bg-white mb-6">
            <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4" data-testid="navhist-maps-toggle">
                <div className="flex items-center gap-2">
                    <LinkIcon size={16} className="text-oth-ink2" />
                    <div className="font-display text-base font-semibold tracking-tight">Saved AMFI → SIF mappings</div>
                    <span className="ml-2 px-2 py-0.5 rounded-full bg-oth-bg-2 text-oth-ink2 text-[10px] font-body">{items.length}</span>
                </div>
                {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {open && (
                <div className="px-5 pb-5 border-t border-oth-line/60">
                    {items.length === 0 && <div className="py-6 text-center text-oth-ink3 text-sm font-body">No mappings yet. Map any unmatched AMFI scheme in the preview screen to auto-resolve future imports.</div>}
                    {items.map((m) => (
                        <div key={m.amfi_scheme_name_norm} className="flex items-center justify-between py-2 border-b border-oth-line/40 last:border-b-0">
                            <div className="text-[12px] font-body min-w-0">
                                <div className="text-oth-ink2 truncate">{m.amfi_scheme_name}</div>
                                <div className="text-oth-ink3 text-[11px]">→ {m.sif_code} · {m.sif_name}</div>
                            </div>
                            <button onClick={() => remove(m.amfi_scheme_name_norm)} className="p-1.5 rounded-full hover:bg-red-50 text-red-600" data-testid={`navhist-map-remove-${m.amfi_scheme_name_norm}`}>
                                <Trash2 size={13} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/* -------------------------- Main tab -------------------------- */
export default function NavHistoryTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await scAdminNavHistList();
            setItems(r?.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load staged files");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    // Auto-poll every 4s while any stage is still fetching so the UI flips
    // to staged/fetch_failed automatically without the admin refreshing.
    useEffect(() => {
        const anyFetching = items.some((it) => it.status === "fetching");
        if (!anyFetching) return;
        const t = setInterval(async () => {
            try {
                const r = await scAdminNavHistList();
                setItems(r?.items || []);
            } catch { /* ignore */ }
        }, 4000);
        return () => clearInterval(t);
    }, [items]);

    const openStage = async (id) => {
        try { setSelected(await scAdminNavHistGet(id)); }
        catch (e) { toast.error(e?.response?.data?.detail || "Could not load stage"); }
    };
    const del = async (id) => {
        if (!window.confirm("Delete this staged file? This cannot be undone.")) return;
        try { await scAdminNavHistDelete(id); toast.success("Deleted"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    return (
        <div data-testid="navhist-tab">
            <SCSection
                title="SIF Control · NAV History Fetch"
                subtitle="Pull AMFI's 'All NAVs for a date' Excel, preview, and import into the NAV database with duplicate protection."
                testId="navhist-header"
            />
            <FetchCard onFetched={load} />
            <MappingsSection />

            <div className="border border-oth-line/70 rounded-3xl bg-white overflow-hidden">
                <div className="grid grid-cols-[110px_1fr_100px_120px_120px_120px_160px] items-center px-5 py-3 border-b border-oth-line/70 bg-oth-bg-2/40 text-[10px] uppercase tracking-[0.2em] font-body text-oth-ink3">
                    <div>Date</div>
                    <div>File</div>
                    <div>Status</div>
                    <div className="text-right">Rows</div>
                    <div className="text-right">Insert / Update</div>
                    <div className="text-right">Dup / Unmatched</div>
                    <div className="text-right">Actions</div>
                </div>
                {loading && <div className="p-8 text-center text-sm text-oth-ink3 font-body">Loading…</div>}
                {!loading && items.length === 0 && (
                    <div className="p-8 text-center text-sm text-oth-ink3 font-body">
                        No staged files yet. Pick a date above and click <span className="font-medium">Fetch Excel</span> to start.
                    </div>
                )}
                {items.map((it) => {
                    const c = it.counts || {};
                    const isFetching = it.status === "fetching";
                    const isFailed = it.status === "fetch_failed" || it.status === "parse_failed";
                    return (
                        <div key={it.id} className={`grid grid-cols-[110px_1fr_100px_120px_120px_120px_160px] items-center px-5 py-3 border-b border-oth-line/40 last:border-b-0 hover:bg-oth-bg-2/40 ${isFetching ? "bg-purple-50/40" : ""}`} data-testid={`navhist-row-${it.id}`}>
                            <div className="text-[12px] font-body font-medium text-oth-ink">{fmtISODate(it.target_date)}</div>
                            <div className="text-[12px] font-body text-oth-ink2 truncate pr-3">
                                <div className="truncate flex items-center gap-2">
                                    {isFetching && <RefreshCw size={11} className="animate-spin text-purple-600 shrink-0" />}
                                    {it.filename}
                                </div>
                                <div className="text-oth-ink3 text-[10px]">
                                    {fmtDate(it.created_at)}
                                    {isFailed && it.error_message && (
                                        <span className="ml-2 text-red-700">— {it.error_message.slice(0, 120)}</span>
                                    )}
                                </div>
                            </div>
                            <div><Pill text={it.status?.replace("_", " ")} cls={STATUS_STYLES[it.status]} /></div>
                            <div className="text-right text-[12px] font-body tabular-nums">{isFetching ? "…" : (it.rows_total || 0)}</div>
                            <div className="text-right text-[12px] font-body tabular-nums text-oth-ink2">
                                {isFetching ? "…" : <><span className="text-green-700">+{c.insert || 0}</span> / <span className="text-blue-700">~{c.update || 0}</span></>}
                            </div>
                            <div className="text-right text-[12px] font-body tabular-nums text-oth-ink2">
                                {isFetching ? "…" : <><span className="text-oth-ink3">={c.skip_duplicate || 0}</span> / <span className="text-red-700">?{c.unmatched || 0}</span></>}
                            </div>
                            <div className="flex items-center justify-end gap-1.5">
                                <button onClick={() => openStage(it.id)} disabled={isFetching} className="px-2.5 py-1 rounded-full bg-oth-blue text-white text-[11px] font-body disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`navhist-preview-${it.id}`}>Preview</button>
                                <a href={scAdminNavHistDownloadUrl(it.id)} target="_blank" rel="noreferrer" className={`p-1.5 rounded-full hover:bg-oth-bg-2 text-oth-ink2 ${isFetching ? "pointer-events-none opacity-40" : ""}`} title="Download XLSX" data-testid={`navhist-dl-${it.id}`}>
                                    <Download size={13} />
                                </a>
                                <button onClick={() => del(it.id)} className="p-1.5 rounded-full hover:bg-red-50 text-red-600" title="Delete" data-testid={`navhist-del-${it.id}`}>
                                    <Trash2 size={13} />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <PreviewModal
                stage={selected}
                onClose={() => setSelected(null)}
                onImported={load}
                onChanged={load}
            />
        </div>
    );
}
